import { useRef } from "react";

/**
 * Return type for the useDownshiftEnvironment hook
 * @property {React.RefObject<HTMLDivElement>} ref - Ref to attach to the wrapper element
 * @property {object | undefined} environment - Environment configuration for downshift
 */
export interface UseDownshiftEnvironmentReturn {
  ref: React.RefObject<HTMLDivElement>;
  environment:
    | {
        document: Document;
        addEventListener: typeof document.addEventListener;
        removeEventListener: typeof document.removeEventListener;
        Node: typeof Node;
      }
    | undefined;
}

/**
 * Hook to handle downshift environment configuration for shadow DOM support
 *
 * Features:
 * - Fixes shadow DOM compatibility issues with downshift
 * - Provides proper document and event listener references
 * - Returns ref for wrapper element and environment configuration
 *
 * @returns Object containing ref and environment configuration
 *
 * @example
 * const { ref, environment } = useDownshiftEnvironment();
 *
 * return (
 *   <div ref={ref}>
 *     <Combobox environment={environment}>
 *       combobox content
 *     </Combobox>
 *   </div>
 * );
 */
export const useDownshiftEnvironment = (): UseDownshiftEnvironmentReturn => {
  // fix for shadow DOM, see https://gist.github.com/Rendez/1dd55882e9b850dd3990feefc9d6e177
  const wrapperDivRef = useRef<HTMLDivElement>(null);

  const shadowRoot = wrapperDivRef.current
    ? wrapperDivRef.current.getRootNode()
    : null;

  const shadowDocument =
    wrapperDivRef.current && shadowRoot ? shadowRoot.ownerDocument : null;

  const environment = shadowDocument
    ? {
        document: shadowDocument,
        addEventListener: shadowDocument.addEventListener.bind(shadowRoot),
        removeEventListener:
          shadowDocument.removeEventListener.bind(shadowRoot),
        Node,
      }
    : undefined;

  return { ref: wrapperDivRef, environment };
};
